import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyIcon } from '@phosphor-icons/react/dist/csr/Key';
import { QuestionIcon } from '@phosphor-icons/react/dist/csr/Question';
import { SignInIcon } from '@phosphor-icons/react/dist/csr/SignIn';

import { brandLogoSrc } from '../../app/shell/brandLogo';
import { useAppearance } from '../../design-system/providers/appearance';

import { Button } from '../../design-system/primitives/Button/Button';
import { Input } from '../../design-system/primitives/Input/Input';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
  VALIDATION_FAILED_CODE,
  api,
  type ApiUser,
  type MfaChallenge,
} from '../../lib/api/client';
import styles from './LoginPage.module.css';

/**
 * The sign-in screen.
 *
 * It renders OUTSIDE `AppShell`, deliberately (task 24 §11.2): a shell whose
 * nav goes nowhere is a nav that lies, and every one of those links 401s.
 * It owns its own `<h1>`, because it is the whole page.
 *
 * ## What the backend already decided, and this screen must not undo
 *
 * The API equalises every credential failure — unknown account, wrong password,
 * disabled account — into one `422 validation_failed`, with the same message,
 * on the same field, in the same time, and it spends a throwaway bcrypt on the
 * missing-account branch to make the timing match. The OpenAPI document says it
 * in capitals. So:
 *
 * - the failure message is ONE string and never names a field;
 * - the server's `details.username[0]` is deliberately NOT rendered under the
 *   username input, even though that is where the API puts it. Painting a
 *   field-scoped error there tells the agent the username was wrong, which the
 *   response does not say and cannot say. It is not an enumeration oracle —
 *   the same thing happens for a wrong password — but it is a lie, and it is
 *   the shape a lazy client turns into one;
 * - there is no "user not found" branch anywhere in this file.
 *
 * ## The copy is ours, not the envelope's
 *
 * `error.message` is a fixed English string per code that the specification
 * says is "not localised". Rendering it puts English in front of an Arabic
 * agent, and for a 500 it puts "Quote this reference to support" on screen. The
 * screen branches on `error.code` and supplies translated copy; the
 * `request_id` is rendered beside it, because that IS the reference and it is
 * the only thing that joins what the agent saw to the row in
 * `application_logs`.
 *
 * ## Why submission is blocked when a field is empty
 *
 * Not for validation's sake — the server validates. For the throttle. An empty
 * username still spends one of the account's five attempts AND one of the
 * address's twenty, and a shared NAT egress means twenty stray Enters across a
 * call-centre floor lock everyone out for fifteen minutes. The plan's own
 * sample tests click Sign in with both fields empty and expect a request; that
 * is the behaviour this deliberately does not have (report §8).
 */

export interface LoginPageProps {
  /** The password alone was enough: a session cookie exists. */
  onSignedIn: (user: ApiUser) => void;
  /**
   * The password was right and it is not enough. The caller renders the second
   * step and holds the token in React state — never in storage, never in a
   * cookie. See `MfaChallenge` in the client.
   *
   * No `remember` argument (Task 4, M-18, round 1 review fix for m-3): the
   * checkbox's value already reached the server on THIS request's own
   * `api.login()` call, which stamps it onto the challenge the API just
   * issued. Nothing here needs to carry it onward — `mfaApi.verify()`
   * derives it from the challenge row instead.
   */
  onChallenged: (challenge: MfaChallenge) => void;
  /** Opens `ForgotPasswordScreen`, so a caller can ask for a reset code. */
  onForgotPassword: () => void;
  /**
   * Opens `ResetPasswordScreen` directly, with an EMPTY address — decision
   * R-8. A code lives in a mailbox for sixty minutes and people close tabs;
   * without this second door, reaching that screen with a code already in
   * hand would mean requesting a fresh one first, which invalidates the one
   * just received.
   */
  onHaveCode: () => void;
  /**
   * A one-off notice to show above the form — today, only ever
   * `ResetPasswordScreen`'s "your password was changed" message, set by
   * `App.tsx` for exactly the render that follows a successful reset.
   * `undefined` renders nothing.
   */
  notice?: string;
}

type FieldName = 'username' | 'password';

export function LoginPage({
  onSignedIn,
  onChallenged,
  onForgotPassword,
  onHaveCode,
  notice,
}: LoginPageProps) {
  const { t } = useTranslation();
  const { theme } = useAppearance();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /**
   * The RENDERED notice — deliberately NOT `notice` itself. See the effect
   * below (task 7 review, Important 3): `notice` is a prop already fixed by
   * `App.tsx` before this component ever mounts, so painting it straight
   * into JSX on the first render would insert the `.notice` region and its
   * text in the SAME commit — the "born with content" shape
   * `src/test/announcements.ts` records as `reliable: false`. This state is
   * what the empty region below actually reads.
   */
  const [announcedNotice, setAnnouncedNotice] = useState<string | undefined>(undefined);

  /** The one form-level message. Never scoped to a field. */
  const [failure, setFailure] = useState<string | null>(null);
  /** The support reference from the envelope, when the failure carried one. */
  const [reference, setReference] = useState<string | null>(null);
  /** Which fields the agent left empty. Drives the field marks, not the copy. */
  const [missing, setMissing] = useState<readonly FieldName[]>([]);
  /** Polite progress, for a screen reader that cannot see a spinner. */
  const [progress, setProgress] = useState<string | null>(null);

  /**
   * Epoch milliseconds until which the API has told us to stop asking. Held in
   * a ref as well as read from it: the value is consulted inside the submit
   * handler, where a stale render's closure would let a blocked attempt through.
   */
  const throttledUntil = useRef(0);

  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  /**
   * Fills `announcedNotice` a macrotask AFTER the region that will hold it
   * is already on the page — task 7 review, Important 3.
   *
   * A plain assignment on the first render (`useState(notice)`, or setting
   * it synchronously in this same effect) is not enough: React/RTL's `act()`
   * flushes passive effects in the SAME synchronous pass as the initial
   * commit, so the `.notice` region and its text would still land in one
   * DOM mutation as far as anything watching (a real screen reader's own
   * diffing, or this codebase's `recordAnnouncements()`) can tell —
   * `reliable: false`. `setTimeout(0)` crosses a genuine task boundary: the
   * empty region commits first, and only afterwards does its text arrive as
   * a change INSIDE a region already being watched — `reliable: true`, and
   * proved as such in `LoginPage.test.tsx`.
   */
  useEffect(() => {
    if (notice === undefined) {
      setAnnouncedNotice(undefined);
      return;
    }
    const handle = window.setTimeout(() => {
      setAnnouncedNotice(notice);
    }, 0);
    return () => {
      window.clearTimeout(handle);
    };
  }, [notice]);

  /** Seconds still to wait, rounded up, never negative. */
  function secondsRemaining(): number {
    return Math.max(0, Math.ceil((throttledUntil.current - Date.now()) / 1000));
  }

  function waitMessage(seconds: number): string {
    if (seconds <= 0) return t('auth.throttled');
    if (seconds >= 60) return t('auth.throttledMinutes', { count: Math.ceil(seconds / 60) });
    return t('auth.throttledSeconds', { count: seconds });
  }

  function describe(error: ApiError): string {
    switch (error.code) {
      case VALIDATION_FAILED_CODE:
        return t('auth.invalidCredentials');
      case TOO_MANY_REQUESTS_CODE:
        return waitMessage(error.retryAfterSeconds ?? 0);
      case PAGE_EXPIRED_CODE:
        // Survived a token refresh and a retry, so it is a real failure.
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('auth.unexpected');
    }
  }

  function fail(message: string, requestId: string | null): void {
    setFailure(message);
    setReference(requestId);
    setProgress(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    // FIRST statement. Everything below can throw; a default-action submit that
    // got through would navigate, and on a GET form it would put the password
    // in the address bar, the history and the referrer. `method="post"` on the
    // element is the second half of that guard.
    event.preventDefault();

    if (submitting) return;

    const empty: FieldName[] = [];
    if (username.trim() === '') empty.push('username');
    if (password === '') empty.push('password');

    if (empty.length > 0) {
      setMissing(empty);
      fail(t('auth.missingFields'), null);
      // The field marks are announced unreliably (they are born with their
      // content); moving focus to the first one is what makes the failure
      // actionable without hunting.
      (empty[0] === 'username' ? usernameRef : passwordRef).current?.focus();
      return;
    }

    setMissing([]);

    const remaining = secondsRemaining();
    if (remaining > 0) {
      // The budget is the agent's, not ours to spend. Re-announce with the
      // time recomputed, rather than leaving a stale number on screen or
      // disabling a control that then explains nothing.
      fail(waitMessage(remaining), null);
      return;
    }

    setSubmitting(true);
    setFailure(null);
    setReference(null);
    setProgress(t('auth.signingIn'));

    try {
      const result = await api.login(username, password, remember);
      // Dropped before the parent gets a chance to swap the tree. Unmounting
      // would discard it anyway; this covers the case where it does not.
      setPassword('');

      /*
       * A correct password buys EITHER a session or a challenge. The branch is
       * on the client's discriminated union rather than on a status here, so
       * this screen cannot be the place that gets 200-versus-202 wrong — the
       * client decides once, and only a 200 is a session.
       *
       * Note what does NOT happen on the challenge path: `auth.signedIn` is not
       * announced and `onSignedIn` is not called. Announcing "signed in" to a
       * screen-reader user who is one factor short would be a lie, and calling
       * the parent's sign-in handler would put the shell on screen with no
       * session behind it.
       */
      if (result.outcome === 'challenged') {
        setProgress(null);
        onChallenged(result.challenge);
        return;
      }

      setProgress(t('auth.signedIn'));
      onSignedIn(result.user);
    } catch (error) {
      if (!(error instanceof ApiError)) {
        // A fault in our own code, not a refusal from the API. Two things have
        // to happen and neither is optional: the agent must be told the form
        // did nothing — silence is worse than any message — and the fault must
        // NOT be disguised as a credential problem, which would blame the user
        // for our bug and hide it from every error reporter. So: the generic
        // sentence, and then let it go on rejecting.
        fail(t('auth.unexpected'), null);
        throw error;
      }

      if (error.code === TOO_MANY_REQUESTS_CODE) {
        throttledUntil.current = Date.now() + (error.retryAfterSeconds ?? 0) * 1000;
      }

      fail(describe(error), error.requestId ?? null);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={styles['page']} data-testid="loginPage">
      <form
        className={styles['card']}
        // Not decoration. If anything above `preventDefault` ever throws, the
        // browser performs the default submit — and a form with no `method`
        // defaults to GET, which would serialise the password into the URL.
        method="post"
        // The constraint is announced through `aria-required` (Field); native
        // validation would replace this screen's copy with a browser bubble in
        // the browser's language.
        noValidate
        onSubmit={(event) => void handleSubmit(event)}
      >
        {/*
          The brand, above the form's own heading.

          `alt=""` + `aria-hidden` HERE, unlike the sidebar's copy of the same
          image — and the difference is the surrounding text, not a
          preference. This screen already has an `<h1>` and an intro paragraph
          naming the product; a named image above them would announce the brand
          a second time before a caller reached either. In the sidebar the
          wordmark is the only naming element there is, so it takes the `alt`.
        */}
        <img className={styles['logo']} src={brandLogoSrc(theme)} alt="" aria-hidden="true" />
        <h1 className={styles['title']}>{t('auth.signIn')}</h1>
        <p className={styles['intro']}>{t('auth.intro')}</p>

        {/*
          THREE live regions, all mounted from the first frame, EMPTY — this
          one included. Task 7 review, Important 3: an earlier version of
          this region cited `App.tsx`'s `sessionCheck` as precedent for
          mounting WITH its text, but that analogy does not hold — that
          region exists for a boot state nobody navigates to, so its text
          being present at the first frame ANYONE sees is not a later change
          for anything to miss. This region is different: it is the sentence
          telling an agent their password was reset, shown the moment they
          land back here, quite possibly after a submit that dropped focus
          to `<body>`. It is empty here and filled by the effect above,
          `setTimeout`-deferred past the mount that carries `notice`, so the
          region already exists — and would already be painted, in a real
          browser — before its text arrives as a change inside it.

          Assistive technology announces changes inside a region it is already
          watching; a region inserted with its message already in it gives the
          platform nothing to diff, and may be announced late or not at all.
          That is task 23's finding, and `src/test/announcements.ts` is what
          proves this screen is on the right side of it for all three regions.

          `aria-atomic` is explicit and it is `true` here, which is the opposite
          of the toast's choice and for the opposite reason: this region holds
          exactly one message plus its support reference, and they have to be
          read as one thing. A toast stack holds several and must not re-read
          them all.
        */}
        <div className={styles['notice']} role="status" aria-atomic="true" data-testid="loginNotice">
          {announcedNotice === undefined ? null : (
            <span className={styles['noticeText']}>{announcedNotice}</span>
          )}
        </div>

        <div className={styles['alert']} role="alert" aria-atomic="true" data-testid="loginAlert">
          {failure === null ? null : <span className={styles['alertText']}>{failure}</span>}
          {reference === null ? null : (
            <span className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              {/*
                Handoff, Arabic & RTL: "Never mirrors — wrap each in dir='ltr':
                ... code identifiers." `<bdi>` rather than a `dir` on the whole
                line, so the Arabic sentence beside it still reads right-to-left
                — and `<bdi>` is what isolates the id's bidi run from the
                punctuation around it, which a bare `<span dir>` does not.
              */}
              <bdi dir="ltr" data-testid="loginRequestId">
                {reference}
              </bdi>
            </span>
          )}
        </div>

        <div className={styles['progress']} role="status" aria-atomic="true" data-testid="loginStatus">
          {progress}
        </div>

        <Input
          ref={usernameRef}
          label={t('auth.username')}
          width="fill"
          name="username"
          // The browser and the password manager both key on this. `off` would
          // not stop either of them and would break the manager that agents
          // actually use.
          autoComplete="username"
          // A username is an identifier, not prose: autocorrecting it on a
          // tablet turns a real login into a credential failure.
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          error={missing.includes('username') ? t('form.requiredField') : undefined}
          value={username}
          onChange={(event) => {
            setUsername(event.target.value);
          }}
        />

        <Input
          ref={passwordRef}
          label={t('auth.password')}
          width="fill"
          type="password"
          name="password"
          autoComplete="current-password"
          required
          error={missing.includes('password') ? t('form.requiredField') : undefined}
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />

        {/*
          Task 4 (M-18). A plain checkbox, not a design-system primitive —
          there is no Checkbox/Switch primitive in this system yet, so this
          mirrors the one other place the codebase already does this
          (`RoleCheckboxes.tsx`'s `<label><input type="checkbox">`), rather
          than inventing a second convention for the same control.

          Unchecked by default and ALWAYS sent as an explicit `false` — see
          `api.login()` — never omitted, so "left unchecked" is a real
          answer this form gives, not a field the request happens to lack.
        */}
        <label className={styles['remember']}>
          <input
            type="checkbox"
            className={styles['rememberCheckbox']}
            checked={remember}
            onChange={(event) => {
              setRemember(event.target.checked);
            }}
          />
          {t('auth.rememberMe')}
        </label>

        <Button icon={<SignInIcon size={16} />} type="submit" loading={submitting} className={styles['submit']}>
          {t('auth.signIn')}
        </Button>

        {/*
          Two doors into account recovery — neither is an anchor. This SPA
          has no router (this module's own docblock references the same
          constraint `App.tsx` documents for itself), and an `href` would
          leave it: a real navigation, a lost `challenge`/`usingRecoveryCode`
          state, a URL this application never intended to answer.
        */}
        <div className={styles['recoveryLinks']} data-testid="recoveryLinks">
          <Button icon={<QuestionIcon size={16} />} type="button" variant="ghost" onClick={onForgotPassword}>
            {t('auth.forgotPassword')}
          </Button>
          <Button icon={<KeyIcon size={16} />} type="button" variant="ghost" onClick={onHaveCode}>
            {t('auth.haveResetCode')}
          </Button>
        </div>
      </form>
    </main>
  );
}
